import React, { createContext, useContext, ReactNode } from "react";

// Define the type for the context value with pre-generated URLs
interface ApiConfigContextType {
  apiUrls: {
    getAssistance: (conversationId: string) => string;
    getConversation: (id: string) => string;
    getResponseStream: (messageId: string, authToken: string) => string;
    getConversations: (type: string, scope: string) => string;
    createConversation: string;
    deleteConversation: (conversationId: string) => string;
    getMessages: (conversation?: string) => string;
    getMessageMediaById: (messageId: string, mediaId: string) => string;
    createMessage: string;
    getEmails: string;
    getEmail: (emailId: string) => string;
    getMessageMedia: (messageId: string) => string;
    getMessageTags: (messageId: string) => string;
    createMessageTag: (messageId: string) => string;
    deleteMessageTag: (messageId: string, tagId: string) => string;
    getTag: (name: string) => string;
    getTagValue: (path: string) => string;
    getContacts: string;
    //getContactTags: (contactId: string) => string;
    //createContactTag: (contactId: string) => string;
    createContactSummary: (contactId: string) => string;
    //deleteContactTag: (contactId: string, tagId: string) => string;
  };
}

// Create the context with a default value of undefined
const ApiConfigContext = createContext<ApiConfigContextType | undefined>(undefined);

// Define the provider component with props
interface ApiConfigProviderProps {
  children: ReactNode;
}

export const ApiConfigProvider: React.FC<ApiConfigProviderProps> = ({ children }) => {
  //api host defaults to empty, which implies same domain as client
  const baseUrl = import.meta.env.VITE_CONSOLE_API_HOST ?? "";

  const apiUrls = {
    getAssistance: (conversationId: string) => {
      return `${baseUrl}/api/assistance/conversations/${conversationId}/run`;
    },
    getConversation: (id: string) => {
      return `${baseUrl}/api/messaging/conversations/${id}`;
    },
    getResponseStream: (messageId: string, authToken: string) => {
      //return `${baseUrl}/api/assistance/conversations/${conversation}/stream?token=${authToken}`;
      return `${baseUrl}/api/messaging/responses/${messageId}/sse?token=${authToken}`;
    },
    getConversations: (type: string, scope: string) => {
      return `${baseUrl}/api/messaging/conversations?scope=${scope}&type=${type}`;
    },
    createConversation: `${baseUrl}/api/messaging/conversations`,
    deleteConversation: (conversationId: string) => {
      return `${baseUrl}/api/messaging/conversations/${conversationId}`;
    },
    getMessages: (conversation?: string) => {
      return `${baseUrl}/api/messages/inbox?conversation=${conversation}`;
    },
    getMessageMediaById: (messageId: string, mediaId: string) => {
      return `${baseUrl}/api/messages/${messageId}/media/${mediaId}`;
    },
    getMessageMedia: (messageId: string) => {
      return `${baseUrl}/api/messages/${messageId}/media`;
    },
    getEmails: `${baseUrl}/api/emails/inbox`,
    getEmail: (emailId: string) => {
      return `${baseUrl}/api/emails/${emailId}`;
    },
    getMessageTags: (messageId: string) => {
      return `${baseUrl}/api/messages/${messageId}/tags`;
    },
    createMessageTag: (messageId: string) => {
      return `${baseUrl}/api/messages/${messageId}/tags`;
    },
    deleteMessageTag: (messageId: string, tagId: string) => {
      return `${baseUrl}/api/messages/${messageId}/tags/${tagId}`;
    },
    createMessage: `${baseUrl}/api/messaging/messages`,
    getTag: (name: string) => {
      return `${baseUrl}/api/tags/${name}`;
    },
    getTagValue: (path: string) => {
      return `${baseUrl}/api/${path}`;
    },
    getContacts: `${baseUrl}/api/directory/contacts`,
    //getContactTags: (contactId: string) => {
    //  return `${baseUrl}/api/directory/contacts/${contactId}/tags`;
    //},
    //createContactTag: (contactId: string) => {
    //  return `${baseUrl}/api/directory/contacts/${contactId}/tags`;
    //},
    createContactSummary: (contactId: string) => {
      return `${baseUrl}/api/directory/contacts/${contactId}/summary`;
    },
    //deleteContactTag: (contactId: string, tagId: string) => {
    //  return `${baseUrl}/api/directory/contacts/${contactId}/tags/${tagId}`;
    //},
  };

  return <ApiConfigContext.Provider value={{ apiUrls }}>{children}</ApiConfigContext.Provider>;
};

// Custom hook to use API configuration
export const useApiConfig = (): ApiConfigContextType => {
  const context = useContext(ApiConfigContext);
  if (!context) {
    throw new Error("useApiConfig must be used within an ApiConfigProvider");
  }
  return context;
};
